import { useEffect, useState } from "react";
import { Button, Checkbox, Popover } from "@mantine/core";
import moment from "moment";
import { CircleX, ListFilterPlus, Search } from "lucide-react";

import {
  EButton,
  EDateInput,
  EMultiSelect,
  EPopover,
  ESelect,
  ETextInput,
  ENumberInput,
  ETooltip
} from "libs/ui";
import { IFilterState } from "types";
import { IMasterData } from "libs/data-access/types";

interface IProps {
  btnLoading?: boolean;
  filter: IFilterState;
  setFilter: (filter: IFilterState) => void;
  onChange: (
    key: string,
    value: string | number | boolean | string[] | number[]
  ) => void;
  onReset?: () => void;
  onSubmit?: () => void;
};

export default function BookingFilter(props: IProps) {

  // const filterButtonRef = useRef<HTMLButtonElement>(null);

  const filterKeys: string[] = Object.keys(props.filter) ?? [];

  const [filterOptionWithKey, setFilterOptionWithKey] = useState<{ [key: keyof IFilterState]: IMasterData[] }[]>([]);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");


  useEffect(() => {
    filterKeys?.map(async (eachFilter) => {
      if (props.filter[eachFilter]?.getDropDownListFunction) {
        props.filter[eachFilter]
          ?.getDropDownListFunction?.()
          .then((response) => {
            setFilterOptionWithKey((prev) => ({
              ...prev,
              [eachFilter]: response ?? [],
            }));
          });
      } else {
        console.log("Please provide getDropDownListFunction");
      }
    });
  }, []);


  // Handle search query changes
  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);
    // We could add debounce here for better performance

    // If we want to apply the search directly to an existing filter field
    // props.onChange('searchTerm', value);

    // Or we can add a new search mechanism here
    // This depends on how the application handles search
  };

  // Get active filters
  const getActiveFilters = () => {
    return filterKeys.filter(
      (key) =>
        props.filter[key]?.value &&
        props.filter[key]?.value !== "" &&
        key !== "searchQuery" // Exclude the search query from filter chips
    );
  };

  // Clear a specific filter
  const clearFilter = (key: string) => {
    props.onChange(key, "");
  };

  // Format filter value for display
  const formatFilterValue = (key: string, value: string) => {
    if (props.filter[key]?.type === "Single" && props.filter[key]?.option) {
      const option = props.filter[key]?.option?.find(
        (opt) => opt.value === value
      );
      return option?.label || value;
    }
    if (props.filter[key]?.type === "Date") {
      return moment(value).format("DD MMM YYYY");
    }
    return value;
  };

  // Handle search button click
  const handleSearch = () => {
    // First apply the search query if it exists
    if (searchQuery) {
      // Assuming we have a 'searchTerm' in the filter, apply it
      // If not, you may need to add this field in your filter state
      props.onChange("searchTerm", searchQuery);
    }

    // Then call the original submit function
    props.onSubmit?.();
  };


  return (
    <div className="p-5 bg-gray-100 rounded-xl border border-gray-200 mb-4">
      {/* Main search bar and filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-3 w-full ">
        {/* Service dropdown - moved outside popover with custom rendering */}
        {props.filter["service"] && (
          <div className="w-48">
            <ESelect
              placeholder="All services"
              value={props.filter["service"]?.value || null}
              data={
                props.filter["service"]?.option
                  ? props.filter["service"]?.option
                  : filterOptionWithKey["service" as any] ?? []
              }
              onChange={(e) => {
                props.onChange("service", e ?? "");
              }}
              radius="md"
              {...props.filter["service"]?.additionalProps}
            />
          </div>
        )}

        {/* Booking Status dropdown - moved outside popover with custom rendering */}
        {props.filter["bookingStatus"] && (
          <div className="w-48">
            <ESelect
              placeholder="All bookings"
              value={props.filter["bookingStatus"]?.value || null}
              data={
                props.filter["bookingStatus"]?.option
                  ? props.filter["bookingStatus"]?.option
                  : filterOptionWithKey["bookingStatus" as any] ?? []
              }
              onChange={(e) => {
                props.onChange("bookingStatus", e ?? "");
              }}
              radius="md"
              {...props.filter["bookingStatus"]?.additionalProps}
            />
          </div>
        )}

        {props.filter["pnr"]
          ?
          <ETextInput
            radius="md"
            className="flex-grow"
            placeholder="Search by pnr..."
            leftSection={<Search size={16} />}
            // value={props.filter["pnr"]?.value || undefined}
            onChange={(e) => {
              props.onChange("pnr", e.target.value);
            }}
            {...props.filter?.additionalProps}
          />
          :
          <></>}

        {/* <ETextInput
          placeholder="Search by names, contact or location..."
          className="flex-grow"
          leftSection={<Search size={16} />}
          value={searchQuery}
          onChange={(e) => handleSearchQueryChange(e.target.value)}
          radius="md"
        /> */}

        <EPopover
          position="bottom-end"
          shadow="xl"
          radius="lg"
          width={400}
          closeOnClickOutside={true}
          closeOnEscape={true}
          withinPortal={false}
          opened={popoverOpened}
          onChange={setPopoverOpened}
        >
          <Popover.Target>
            <div>
              <EButton
                onClick={() => setPopoverOpened((o) => !o)}
                variant="default"
              >
                <ListFilterPlus size={20} className="mr-2" /> Filters
              </EButton>
            </div>
          </Popover.Target>
          <Popover.Dropdown>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <div className="text-base font-semibold">
                Filters
              </div>
              <ETooltip
                label="Close"
                position="bottom"
                withArrow
                arrowSize={10}
                arrowOffset={10}
                arrowPosition="center"
              >
                <CircleX size={20} className="cursor-pointer" onClick={() => setPopoverOpened(false)} />
              </ETooltip>
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto py-4">
              {filterKeys
                .filter((key) => key !== "service" && key !== "bookingStatus") // Exclude the filters we moved out
                .map((eachFilter) => {
                  const filterItem = props.filter[eachFilter];
                  return (
                    <div key={eachFilter}>
                      {renderFilterOption(eachFilter, filterItem)}
                    </div>
                  );
                })}
            </div>
          </Popover.Dropdown>
        </EPopover>

        {/* <Menu
          opened={popoverOpened}
          onChange={setPopoverOpened}
          position="bottom-end"
          shadow="xl"
          radius="lg"
          width={320}
          closeOnClickOutside
          closeOnEscape
        >
          <Menu.Target>
            <div>
              <EButton
                onClick={() => setPopoverOpened((o) => !o)}
                variant="default"
              >
                <ListFilterPlus size={20} className="mr-2" /> Filters
              </EButton>
            </div>
          </Menu.Target>

          <Menu.Dropdown>
            <div className="text-base font-semibold p-4 py-2 border-b border-gray-200">
              Filters
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto p-4">
              {filterKeys
                .filter((key) => key !== "service" && key !== "bookingStatus") // Exclude the filters we moved out
                .map((eachFilter) => {
                  const filterItem = props.filter[eachFilter];
                  return (
                    <div key={eachFilter}>
                      {renderFilterOption(eachFilter, filterItem)}
                    </div>
                  );
                })}
            </div>
          </Menu.Dropdown>
        </Menu> */}

        <EButton loading={props.btnLoading} onClick={handleSearch}>
          Search
        </EButton>
      </div>

      {/* Filter chips/tags */}
      <div className="flex flex-wrap gap-2 w-full">
        {getActiveFilters().map((key) => (
          <div
            key={key}
            className="flex items-center py-1 px-3 bg-white shadow-sm border border-slate-300 rounded-md text-sm text-slate-900"
          >
            <span className="font-medium mr-1">{props.filter[key].label}:</span>
            <span>
              {formatFilterValue(key, props.filter[key].value as string)}
            </span>
            <button
              onClick={() => clearFilter(key)}
              className="ml-2 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        ))}

        {getActiveFilters().length > 0 && (
          <Button
            variant="subtle"
            size="xs"
            className="text-blue-500 "
            style={{ fontSize: "14px" }}
            onClick={props.onReset}
          >
            Reset All
          </Button>
        )}
      </div>
    </div>
  );

  // Helper function to render filter options in the popover
  function renderFilterOption(key: string, filter: any) {
    switch (filter?.type) {
      case "Text":
        return (
          <ETextInput
            clearable
            label={filter.label}
            placeholder={"Enter " + filter.label}
            value={filter?.value}
            onChange={(e) => {
              props.onChange(key, e.target.value);
            }}
            {...filter?.additionalProps}
          />
        );
      case "Number":
        return (
          <ENumberInput
            clearable
            label={filter.label}
            placeholder={"Enter " + filter.label}
            value={filter?.value as string}
            onChange={(e) => {
              props.onChange(key, e);
            }}
            {...filter?.additionalProps}
          />
        );
      case "Multiple":
        return (
          <EMultiSelect
            clearable
            comboboxProps={{ withinPortal: false }}
            label={filter.label}
            placeholder="Select"
            value={filter?.value ? filter?.value?.split(",") : []}
            data={filter?.option ?? []}
            onChange={(e) => {
              props.onChange(key, e ? e.join(",") : "");
            }}
            {...filter?.additionalProps}
          />
        );
      case "Single":
        return (
          <ESelect
            clearable
            comboboxProps={{ withinPortal: false }}
            placeholder="Select"
            label={filter.label}
            value={filter?.value || null}
            data={
              filter?.option
                ? filter?.option
                : filterOptionWithKey[key as any] ?? []
            }
            onChange={(e) => {
              if (filter?.relationChild?.length) {
                filter?.relationChild?.map((child: string) => {
                  props.filter[child].value = "";
                  props.filter[key].value = e ?? "";
                  props.setFilter(props.filter);
                  if (e) {
                    props.filter[child]
                      ?.getDropDownListByIdFunction?.(e)
                      .then((response) => {
                        setFilterOptionWithKey((prev) => ({
                          ...prev,
                          [key]: response ?? [],
                        }));
                      })
                      .catch((error) => {
                        console.log("error", error);
                        setFilterOptionWithKey((prev) => ({
                          ...prev,
                          [key]: [],
                        }));
                        props.filter[child].option = [];
                      });
                  }
                });
              } else {
                props.onChange(key, e ?? "");
                // setPopoverOpened(false);
              }
            }}
            {...filter?.additionalProps}
          />
        );
      case "CheckBox":
        return (
          <Checkbox
            clearable
            label={filter.label}
            checked={filter?.value === "true"}
            onChange={(e) => {
              props.onChange(key, e.target.checked);
            }}
            {...filter?.additionalProps}
          />
        );
      case "Date":
        return (
          <EDateInput
            clearable
            popoverProps={{ withinPortal: false }}
            label={filter.label}
            placeholder={"Select"}
            value={filter?.value ? new Date(filter?.value) : undefined}
            onChange={(e) => {
              props.onChange(key, e ? moment(e).format("YYYY-MM-DD") : "");
              // setPopoverOpened(false);
            }}
            {...filter?.additionalProps}
          />
        );
      default:
        return (
          <ETextInput
            clearable
            label={filter.label}
            placeholder={"Enter " + filter.label}
            value={filter?.value}
            onChange={(e) => {
              props.onChange(key, e.target.value);
            }}
            {...filter?.additionalProps}
          />
        );
    }
  };
}